import React from 'react';

/**
 * Utility functions for managing format column display modes in tables
 */

/**
 * Determines if format column should use hover mode based on screen size and device type
 * @param {number} width - Current screen width
 * @param {boolean} isTouchDevice - Whether device supports touch
 * @param {boolean} forceHoverMode - Force hover mode regardless of other conditions
 * @returns {boolean} Whether to use hover mode
 */
export const shouldUseFormatColumnHoverMode = (width, forceHoverMode = false) => {
  if (forceHoverMode) return true;
  
  return width <= 1024 && width > 768;
};

export const getAvailableFormatsColumnWidth = (width, defaultWidth = 330) => {
  const baseWidth = defaultWidth || 330;
  return width > 1024 && width < 1580 ? Math.min(baseWidth, 190) : baseWidth;
};

export const getDashboardFormatsColumnWidth = (width, defaultWidth = 330) => {
  const baseWidth = defaultWidth || 330;
  return width > 768 && width < 1580 ? Math.min(baseWidth, 190) : baseWidth;
};

/**
 * Calculates the horizontal scroll width without inflating the zero-width
 * anchor used by hover action columns.
 * @param {array} columns - Processed table column configurations
 * @param {boolean} useHoverMode - Whether the format actions use a hover anchor
 * @returns {number} Horizontal scroll width in pixels
 */
export const getTableScrollWidth = (columns, useHoverMode = false) => {
  const totalWidth = columns.reduce(
    (total, column) => total + (column.width ?? 100),
    0
  );

  return useHoverMode ? totalWidth : totalWidth - 80;
};

/**
 * Gets the format column based on hover mode preference
 * @param {object} formatColumn - Original format column configuration
 * @param {boolean} useHoverMode - Whether to use hover mode
 * @param {number} hoverIndex - Current hover index for row highlighting
 * @returns {object} Processed format column
 */
export const getProcessedFormatColumn = (formatColumn, useHoverMode, hoverIndex) => {
  if (!useHoverMode) {
    return formatColumn;
  }

  return {
    title: () => <div className="centered"></div>,
    width: 0,
    fixed: "right",
    dataIndex: 'fixed-hover-right-hover',
    className: 'hover-format-column',
    render: (_, item, index) => {
      const shouldRender = index === hoverIndex;
      if (shouldRender) {
        return (
          <div className='custom-tooltip'>
            <div className='tooltip-content'>
              <div>
                {formatColumn.render(_, item, index)}
              </div>
            </div>
          </div>
        );
      }
      return <div></div>;
    }
  };
};

/**
 * Transforms columns array to support hover mode for format columns
 * @param {array} columns - Array of column configurations
 * @param {boolean} useHoverMode - Whether to use hover mode
 * @param {number} hoverIndex - Current hover index for row highlighting
 * @returns {array} Processed columns array
 */
export const processColumnsForHoverMode = (columns, useHoverMode, hoverIndex) => {
  if (!useHoverMode) {
    return columns;
  }

  const formatColumnIndex = columns.findIndex(col => col.dataIndex === 'fixed-hover-right');
  if (formatColumnIndex === -1) {
    return columns;
  }

  const formatColumn = columns[formatColumnIndex];
  const hoverColumn = getProcessedFormatColumn(formatColumn, useHoverMode, hoverIndex);

  return [
    ...columns.filter(col => col.dataIndex !== 'fixed-hover-right'),
    hoverColumn
  ];
};
